import axiosInstance from '@/plugins/axios';

/** Turns { start_date, end_date, ... } into a query string, skipping empty values. */
function query(params = {}) {
    const search = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            search.append(key, value);
        }
    });

    const asString = search.toString();
    return asString ? `?${asString}` : '';
}

/** The Log tab's themed side feeds: key → the events each one shows. */
export const LOG_PANELS = {
    money: ['paywall_view', 'paywall_dismiss', 'purchase_start', 'purchase_success', 'purchase_cancel', 'purchase_pending', 'purchase_failed', 'restore_start', 'restore_success', 'feature_locked'],
    ai: ['answer_feedback', 'answer_feedback_reason', 'dream_interpreted', 'dream_blocked'],
    firstSteps: ['onboarding_step', 'onboarding_completed', 'notification_permission', 'location_permission'],
    comeback: ['notification_opened', 'deep_link_opened', 'review_prompt'],
    reading: ['page_read', 'book_viewed', 'book_completed', 'bookmark_added', 'bookmark_removed', 'audio_played', 'one-book-retrieve'],
    time: ['session_end', 'feature_close'],
};

/** Rows per log page. */
const RECENT_PAGE = 100;

async function get(path, params) {
    const response = await axiosInstance.get(`/api/analytics/${path}${query(params)}`);
    return response.data.data;
}

export const actions = {
    /**
     * Loads everything the dashboard shows in one go. Individual failures are
     * tolerated: one broken panel should not blank the whole page.
     */
    async loadAll(params = {}) {
        this.loading = true;
        this.error = null;

        const range = { start_date: params.start_date, end_date: params.end_date };
        this.recentRange = range;
        const interval = params.interval || 'day';

        const results = await Promise.allSettled([
            get('overview', range),
            get('funnel', range),
            get('breakdown', range),
            get('timeseries', { ...range, interval, events: 'app_open,feature_open,paywall_view,paywall_dismiss,purchase_start,purchase_cancel,purchase_failed,purchase_success' }),
            get('paywalls', range),
            get('recent', { limit: RECENT_PAGE, ...range, ...this.recentFilters }),
            get('users-timeseries', { ...range, interval }),
            get('property-breakdown', { ...range, event: 'feature_open', property: 'feature' }),
            get('property-breakdown', { ...range, event: 'feature_locked', property: 'feature' }),
            get('property-breakdown', { ...range, event: 'purchase_success', property: 'product_id' }),
            get('engagement', range),
        ]);

        const [overview, funnel, breakdown, timeSeries, paywalls, recent, users, features, locked, products, engagement] = results;

        if (overview.status === 'fulfilled') this.overview = overview.value;
        if (funnel.status === 'fulfilled') this.funnel = funnel.value || [];
        if (breakdown.status === 'fulfilled') this.breakdown = breakdown.value || [];
        if (timeSeries.status === 'fulfilled') this.timeSeries = timeSeries.value || { labels: [], series: [] };
        if (paywalls.status === 'fulfilled') this.paywalls = paywalls.value || [];
        if (recent.status === 'fulfilled') {
            this.recent = recent.value || [];
            this.recentHasMore = this.recent.length === RECENT_PAGE;
        }
        if (users.status === 'fulfilled') this.users = users.value || { labels: [], series: [] };
        if (features.status === 'fulfilled') this.features = features.value || [];
        if (locked.status === 'fulfilled') this.locked = locked.value || [];
        if (products.status === 'fulfilled') this.products = products.value || [];
        if (engagement.status === 'fulfilled') this.engagement = engagement.value || null;

        const failed = results.find((result) => result.status === 'rejected');

        if (failed) {
            const status = failed.reason?.response?.status;
            this.error =
                status === 403
                    ? 'Your account does not have permission to view analytics.'
                    : status === 404
                        ? 'The server is missing the newest analytics endpoints — deploy the backend to see every panel.'
                        : 'Some analytics could not be loaded.';
        }

        this.loading = false;
    },

    /**
     * The log feed with its own filters. `more` appends the next older page
     * instead of starting over.
     */
    async loadRecent(filters = this.recentFilters, more = false) {
        this.recentFilters = filters;
        this.recentLoading = true;
        try {
            const beforeId = more && this.recent.length ? this.recent[this.recent.length - 1].id : undefined;
            const rows = (await get('recent', { limit: RECENT_PAGE, ...this.recentRange, ...filters, before_id: beforeId })) || [];
            this.recent = more ? [...this.recent, ...rows] : rows;
            this.recentHasMore = rows.length === RECENT_PAGE;
        } catch {
            if (!more) this.recent = [];
        } finally {
            this.recentLoading = false;
        }
    },

    /**
     * Everything on the Log tab apart from the main feed, for one device choice.
     * Each side feed is a fixed set of events from the same `recent` endpoint.
     */
    async loadLog(devices = this.logDevices) {
        this.logDevices = devices;
        const base = { ...this.recentRange, devices };
        const panels = Object.entries(LOG_PANELS);
        const results = await Promise.allSettled([
            get('log-timeline', base),
            get('top-users', { ...base, limit: 15 }),
            get('breakdown', base),
            ...panels.map(([, events]) => get('recent', { ...base, events: events.join(','), limit: 40 })),
        ]);
        const [timeline, top, breakdown, ...feeds] = results;
        if (timeline.status === 'fulfilled') this.logTimeline = timeline.value;
        if (top.status === 'fulfilled') this.logTopUsers = top.value || [];
        if (breakdown.status === 'fulfilled') this.logBreakdown = breakdown.value || [];
        const next = { ...this.logPanels };
        feeds.forEach((result, i) => { if (result.status === 'fulfilled') next[panels[i][0]] = result.value || []; });
        this.logPanels = next;
    },

    /** Live mode: rows newer than the top of the main feed go on top. */
    async pollRecent() {
        if (this.recentLoading) return;
        const newest = this.recent[0]?.id;
        try {
            const rows = (await get('recent', { limit: RECENT_PAGE, ...this.recentRange, ...this.recentFilters, after_id: newest })) || [];
            if (rows.length) this.recent = [...rows, ...this.recent];
        } catch {
            // A missed poll is caught up by the next one.
        }
    },

    async getOverview(params) {
        this.overview = await get('overview', params);
        return this.overview;
    },

    /** January → today, per month, for the widgets that show a year at a glance. */
    async loadMonthly() {
        const now = new Date();
        const iso = (d) => d.toISOString().slice(0, 10);
        try {
            this.monthly = await get('timeseries', {
                start_date: `${now.getFullYear()}-01-01`,
                end_date: iso(now),
                interval: 'month',
                events: 'app_open,feature_open,purchase_success',
            });
        } catch {
            this.monthly = { labels: [], series: [] };
        }
        return this.monthly;
    },

    async getTimeSeries(params) {
        this.timeSeries = await get('timeseries', params);
        return this.timeSeries;
    },

    async getPropertyBreakdown(event, property, params) {
        return get('property-breakdown', { ...params, event, property });
    },

    async getTopContent(params) {
        return get('content', params);
    },
};
